/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { MosaicNode } from 'react-mosaic-component';
import type { InitialProps } from '@opengeoweb/core';
import type { SystemScope } from '@opengeoweb/shared';
import type { syncGroupsTypes } from '@opengeoweb/store';

export type WorkspaceViewPresetComponentsLookupType =
  | 'ViewError'
  | 'ViewLoading';

export interface WorkspaceViewType {
  id?: string;
  mosaicNodeId?: string;
  title?: string; // If not provided, id will be used instead
  description?: string;
  abstract?: string;
  componentType: string;
  initialProps?: InitialProps;
  activeLayerId?: string;
  shouldPreventClose?: boolean;
  scope?: SystemScope;
  keywords?: string;
  labels?: WorkspaceLabelsConfigResponse;
  username?: string;
}

export interface WorkspaceViews {
  byId: Record<string, WorkspaceViewType>;
  allIds: string[];
}

export interface WorkspaceViewsFromBE {
  mosaicNodeId: string;
  viewPresetId?: string;
  componentType?: string;
  initialProps?: InitialProps;
  title?: string;
  originalViewPresetId?: string;
}

export type ViewType = 'singleWindow' | 'multiWindow' | 'tiledWindow';

export interface WorkspaceSyncGroup {
  id: string;
  type: syncGroupsTypes.SyncType;
  title?: string;
  sourcePanelId?: string;
}

export interface WorkspacePreset {
  id?: string;
  title: string;
  syncGroups?: WorkspaceSyncGroup[];
  isTimeScrollingEnabled?: boolean;
  views: WorkspaceViews;
  viewType?: ViewType;
  mosaicNode: MosaicNode<string>;
  minimumPaneSizePercentage?: number;
  scope?: SystemScope;
  abstract?: string;
  description?: string;
  linking?: Record<string, string[]>;
  username?: string;
  labels?: WorkspacePresetLabels;
  updated?: string;
  updatedBy?: string;
}

// should be extracted from storing preset
export interface WorkspaceState extends WorkspacePreset {
  isLoading?: boolean;
  error?: WorkspaceError;
  hasChanges?: boolean;
}

export interface WorkspacePresetLabels {
  [groupKey: string]: string[] | undefined;
  purpose?: string[];
  parameters?: string[];
  phenomena?: string[];
  dataType?: string[];
  dataSource?: string[];
  viewType?: string[];
}

// A plain title, or a title per language code, like { "en": "New map", "no": "Nytt kart" }
export type TemplatePresetTitle = string | Record<string, string>;

export interface TemplatePresetView extends Omit<WorkspaceViewType, 'title'> {
  title?: TemplatePresetTitle;
}

export interface TemplatePresetViews {
  allIds: string[];
  byId: Record<string, TemplatePresetView>;
}

export interface WorkspaceTemplatePreset extends Omit<
  WorkspacePreset,
  'title' | 'views'
> {
  title: TemplatePresetTitle;
  views: TemplatePresetViews;
}

export interface ViewTemplatePreset extends Pick<
  WorkspaceViewType,
  'componentType' | 'initialProps' | 'abstract' | 'description'
> {
  id: string;
  title: TemplatePresetTitle;
  labels?: WorkspacePresetLabels;
}

export interface TemplatePresetsResponse {
  workspacePresets: WorkspaceTemplatePreset[];
  viewPresets: ViewTemplatePreset[];
}

export interface WorkspacePresetForBE {
  id?: string;
  title: string;
  syncGroups?: WorkspaceSyncGroup[];
  linking?: Record<string, string[]>;
  views: WorkspaceViewsFromBE[];
  viewType?: ViewType;
  mosaicNode: MosaicNode<string>;
  scope?: SystemScope;
  is_shared?: boolean;
  username?: string;
  abstract?: string;
  description?: string;
  labels?: WorkspacePresetLabels;
  updated?: string;
  updatedBy?: string;
}
export interface WorkspacePresetFromBE extends WorkspacePresetForBE {
  id: string;
  viewType: ViewType;
  scope: SystemScope;
  username?: string;
}

export interface PresetListItemOrigin {
  type: 'plugin';
  pluginId: string;
  versions?: string[];
  version?: string;
  activeVersion?: string;
}

export interface WorkspacePresetListItem {
  id: string;
  date?: string;
  title: string;
  scope: SystemScope;
  viewType: ViewType;
  abstract: string;
  username?: string;
  is_shared?: boolean;
  labels?: WorkspacePresetLabels;
  description?: string;
  updated?: string;
  updatedBy?: string;
  origin?: PresetListItemOrigin;
  isWorkspaceTemplate?: boolean;
  isViewTemplate?: boolean;
}

export enum WorkspacePresetAction {
  DELETE = 'DELETE',
  EDIT = 'EDIT',
  SAVE = 'SAVE',
  SAVE_AS = 'SAVE_AS',
  DUPLICATE = 'DUPLICATE',
  SAVE_SYSTEM_PRESET = 'SAVE_SYSTEM_PRESET',
  SAVE_SYSTEM_PRESET_AS = 'SAVE_SYSTEM_PRESET_AS',
  RESET = 'RESET',
  DELETE_SYSTEM_PRESET = 'DELETE_SYSTEM_PRESET',
  SHARE = 'SHARE',
  OPEN_TAB = 'OPEN_TAB',
  EDIT_TITLE = 'EDIT_TITLE',
  COPY_TO_CLIPBOARD = 'COPY_TO_CLIPBOARD',
}

// actions
export interface WorkspaceAddViewPayload {
  id: string;
  componentType: string;
  initialProps: InitialProps;
}

export interface WorkspaceSetNewViewPayload {
  mosaicNodeId: string;
  newId: string;
  componentType: string;
  copyOriginalcontent: boolean;
}

export interface WorkspaceDeleteViewPayload {
  id: string;
}

export interface WorkspaceUpdateViewsPayload {
  mosaicNode: MosaicNode<string>;
}

export interface WorkspaceUpdateViewPayload {
  mosaicNodeId: string;
  viewPreset: WorkspaceViewType;
}

export interface WorkspaceChangeToEmptyMapWorkspacePayload {
  newMapPresetText: string;
  newWorkspaceText: string;
}

export interface WorkspaceSetPresetPayload {
  workspacePreset: WorkspacePreset;
  markAsChanged?: boolean;
}

export interface WorkspaceSetPreventCloseViewPayload {
  viewId: string;
  shouldPreventClose: boolean;
}

// base WorkspaceComponentLookupPayload type. This can be extended in libraries to make it more specific
export interface WorkspaceComponentLookupPayload {
  componentType: string;
  id: string;
  title?: string;
  initialProps: unknown;
}

export type WorkspaceLookupFunctionType = (
  payload: WorkspaceComponentLookupPayload,
) => React.ReactElement;

export interface ViewPresetListItem {
  id: string;
  title: string;
  date: string;
  scope: SystemScope;
  abstract?: string;
  description?: string;
  is_shared?: boolean;
  username?: string;
  labels?: WorkspacePresetLabels;
  updated?: string;
  updatedBy?: string;
  isWorkspaceTemplate?: boolean;
  isViewTemplate?: boolean;
}

// Workspaces
export interface WorkspaceFetchWorkspacePayload {
  workspaceId: string;
}
export interface WorkspaceFetchedWorkspacePayload {
  workspaceId: string;
  workspace: WorkspacePresetForBE;
}
export interface WorkspaceFetchWorkspaceViewPresetPayload {
  viewPresetId: string;
  mosaicNodeId: string;
}

export interface WorkspaceSaveWorkspacePresetPayload {
  workspace: WorkspacePresetForBE;
}

export enum WorkspaceErrorType {
  NOT_FOUND = 'NOT_FOUND',
  GENERIC = 'GENERIC',
  SAVE = 'SAVE',
}

export type WorkspaceError =
  | { type: WorkspaceErrorType; message: string }
  | undefined;

export interface WorkspaceErrorWorkspacePayload {
  workspaceId: string;
  error: WorkspaceError;
}

export type WorkspaceLabelsConfigResponse = WorkspacePresetLabels;
